<template>
  <div>
    <Header title="Admin" subtitle="Global Settings"/>
    <template v-if="!loading && user.roles.admin">
      <div :class="$style.items">
        <Card
            title="Settings"
            direction="column"
            color="background"
            background="black"
            spacing="tad"
            as="section"
        >
          <TextComponent as="h2">
            Note that these changes will not be applied until they are confirmed below!
          </TextComponent>

          <Input
              :class="$style.item"
              name="maxAccepted"
              label="Max Accepted"
              type="number"
              v-model="settings.universe.private.maxAccepted"
          />
          <Input
              :class="$style.item"
              name="maxWaitlisted"
              label="Max Waitlisted"
              type="number"
              v-model="settings.universe.private.maxWaitlist"
          />

          <br />
          <TextComponent>
            Global Application Open: <b>{{ new Date(settings.universe.public.globalApplicationOpen).toString() }}</b>
          </TextComponent>
          <Button @click="editDate('globalApplicationOpen')">
            Edit
          </Button>
          <br />

          <TextComponent>
            Global Application Deadline: <b>{{ new Date(settings.universe.public.globalApplicationDeadline).toString() }}</b>
          </TextComponent>
          <Button @click="editDate('globalApplicationDeadline')">
            Edit
          </Button>
          <br />
          <TextComponent>
            Global Confirmation Deadline: <b>{{ new Date(settings.universe.public.globalConfirmationDeadline).toString() }}</b>
          </TextComponent>
          <Button @click="editDate('globalConfirmationDeadline')">
            Edit
          </Button>
          <br />
          <TextComponent>
            Global Waitlist Accepted Confirmation Deadline: <b>{{ new Date(settings.universe.public.globalWaitlistAcceptedConfirmationDeadline).toString() }}</b>
          </TextComponent>
          <Button @click="editDate('globalWaitlistAcceptedConfirmationDeadline')">
            Edit
          </Button>
          <br />

          <Button @click="updateSettings">
            Confirm Changes
          </Button>
        </Card>
      </div>


      <div :class="$style.items">
        <br />
        <Card
            title="Actions"
            direction="row"
            color="background"
            background="black"
            spacing="tad"
            as="section"
        >

          <Button @click="runSyncMailingLists(true)">
            [FORCE] Sync Mailing List
          </Button>
          <Button @click="runSyncMailingLists(false)">
            Sync Mailing List
          </Button>
          <Button @click="runVerifyMailingList">
            Verify Mailing List
          </Button>
          <Button @click="runTemplateTest">
            Run Template Test
          </Button>
          <Button @click="runReleaseApplicationStatus">
            Release application status
          </Button>
          <Button @click="runResumeExport">
            Resume Export
          </Button>
        </Card>
      </div>

      <div :class="$style.items">
        <br />
        <Card
            title="System Actions"
            direction="row"
            color="background"
            background="black"
            spacing="tad"
            as="section"
        >

          <Button @click="generateAPIKey">
            Generate API Key
          </Button>
        </Card>
      </div>

    </template>
    <template v-else-if="!user.roles.admin">
      You do not have access to this page
    </template>
    <template v-else>
      Loading...
    </template>
  </div>
</template>

<script>
  import Button from '@hackthe6ix/vue-ui/Button';
  import Input from '@hackthe6ix/vue-ui/Input';
  import TextComponent from '@hackthe6ix/vue-ui/Text/index';
  import swal from 'sweetalert';
  import Card from '../../../containers/Card';
  import Header from '../../../containers/Header';
  import {
    createAPIToken,
    editObject,
    getObject,
    releaseApplicationStatus,
    resumeExport,
    syncMailingLists,
    templateTest,
    verifyMailingList,
  } from '../../../utils/api';
  import { getUser } from '../../../utils/UserController';

  export default {
    name: 'Admin',
    components: {
      TextComponent,
      Header,
      Button,
      Card,
      Input
    },
    data() {
      return {
        loading: true,
        user: {
          roles: {}
        },
        settings: {}
      }
    },
    methods: {
      runResumeExport() {
        swal({
          title: 'CONFIRM ACTION',
          text: 'Are you sure you want to export resumes? This is a computationally expensive operation',
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (confirm) => {
          if (confirm) {
            const result = await resumeExport();

            if (result.success) {

              const url = window.URL.createObjectURL(
                new Blob([result.data], { type: 'application/zip' }),
              );

              const link = document.createElement('a');
              link.href = url;
              link.download = 'export.zip';

              // this is necessary as link.click() does not work on the latest firefox
              link.dispatchEvent(
                new MouseEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
              );

            } else {
              swal('Error', result.data, 'error');
            }
          }
        });

      },
      runReleaseApplicationStatus() {
        swal({
          title: 'CONFIRM ACTION',
          text: 'Are you sure you want to release application status? This action cannot be easily rolled back and will immediately allow users to see their application status',
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (confirm) => {
          if (confirm) {

            const response = prompt('Type "CONFIRM" to proceed. This is the FINAL WARNING!');

            if (response === 'CONFIRM') {

              const result = await releaseApplicationStatus();

              if (result.success) {
                swal('Success',
                  `Application status has been updated for ${result.data.length} users!`,
                  'success');
              } else {
                swal('Error', result.data, 'error');
              }

            } else {
              swal('Invalid Confirmation', 'Action Aborted', 'error');
            }
          }
        });
      },
      runSyncMailingLists(force) {
        swal({
          title: 'CONFIRM ACTION',
          text: 'Are you sure you want to sync mailing lists? This may cause server loads',
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (confirm) => {
          if (confirm) {
            const result = await syncMailingLists(force);

            if (result.success) {
              swal('Success', 'Mailing list have been synced: ' + result.data.toString(),
                'success');
            } else {
              swal('Error', result.data, 'error');
            }
          }
        });
      },
      runVerifyMailingList() {
        swal({
          title: 'CONFIRM ACTION',
          text: 'Are you sure you want to insert mock users to verify the mailing list configuration?',
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (confirm) => {
          if (confirm) {
            const result = await verifyMailingList();

            if (result.success) {
              swal('Success',
                'Mock users have been inserted into mailtrain: ' + result.data.toString(),
                'success');
            } else {
              swal('Error', result.data, 'error');
            }
          }
        });
      },
      runTemplateTest() {
        swal({
          title: 'CONFIRM ACTION',
          text: 'Are you sure you want to send yourself test emails?',
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (confirm) => {
          if (confirm) {
            const result = await templateTest();

            if (result.success) {
              swal('Success', 'Test emails have been dispatched: ' + result.data.toString(),
                'success');
            } else {
              swal('Error', result.data, 'error');
            }
          }
        });
      },
      editDate(key) {
        swal({
          text: 'Enter new date for ' + key,
          content: {
            element: 'input',
            attributes: {
              defaultValue: new Date(this.settings.universe.public[key]).toLocaleString("en-US"),
            }
          },
          button: {
            text: "Set",
            closeModal: true,
          }
        })
        .then(entry => {
          if (!entry) {
            return;
          }

          const parsedTime = new Date(entry).getTime();

          if (parsedTime) {
            this.settings.universe.public[key] = parsedTime;
            swal('Success', 'Time updated. You will need to save to submit changes!', 'success');
            return null;
          } else {
            swal('Error', 'Unable to parse timestamp', 'error');
            return;
          }
        })
      },
      updateSettings() {
        swal({
          title: 'CONFIRM ACTION',
          text: 'Are you sure you want to update settings? The changes will be applied immediately',
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (confirm) => {
          if (confirm) {
            const result = await editObject('settings', {}, this.settings);

            if (result.success) {
              swal('Success', 'Your changes have been saved', 'success');
            } else {
              swal('Error', result.data, 'error');
            }
          }
        });
      },
      generateAPIKey() {
        swal({
          text: 'Enter description for the API Token',
          content: {
            element: 'input',
            attributes: {
              defaultValue: "An API Token",
            }
          },
          button: {
            closeModal: true,
          }
        })
        .then(async entry => {
          if (!entry) {
            return;
          }

          const result = await createAPIToken(["organizer"], entry);
          if (result.success) {
            swal('Success', `Token: ${result.data.token}`, 'success');
          } else {
            swal('Error', result.data, 'error');
          }
        })
      }
    },
    beforeMount() {
      (async () => {
        this.user = await getUser();

        const settingsResult = await getObject('settings', {});

        if (settingsResult.success) {
          this.settings = settingsResult.data[0] || {};
        } else {
          swal('Error', settingsResult.data, 'error');
        }

        this.loading = false;

      })();
    },
  };
</script>

<style src="./Admin.module.scss" lang="scss" module/>
